import { Link } from "react-router-dom";
import { useWorkItem } from "../api/useWorkItem";
import { ContextSection } from "@/modules/workitem/components/ContextSection";
import { ActivityLog } from "@/modules/workitem/components/ActivityLog";
import { WorkItemActions } from "@/modules/workitem/components/WorkItemActions";
import { Badge } from "@hub/ui/components/ui/badge";
import { Button } from "@hub/ui/components/ui/button";
import { Skeleton } from "@hub/ui/components/ui/skeleton";
import { ScrollArea } from "@hub/ui/components/ui/scroll-area";
import { Maximize2 } from "lucide-react";
import { statusColors } from "@/core/constants/statusColors";

interface DetailPanelProps {
  itemId: string | null;
}

export function DetailPanel({ itemId }: DetailPanelProps) {
  if (!itemId) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
        Select an item to view details
      </div>
    );
  }

  return <DetailPanelContent itemId={itemId} />;
}

function DetailPanelContent({ itemId }: { itemId: string }) {
  const { item, loading } = useWorkItem(itemId);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        Work item not found
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {item.sourceRef || item.id.slice(0, 12)}
              </h2>
              <Badge className={statusColors[item.status] ?? statusColors.open}>
                {item.status.replace(/_/g, " ")}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {item.type} &middot; {item.claimedBy ? `Claimed by ${item.claimedBy}` : "Unclaimed"}
            </p>
          </div>
          <Link to={`/queue/${item.id}`}>
            <Button variant="outline" size="sm">
              <Maximize2 className="h-4 w-4 mr-1" />
              Open Full
            </Button>
          </Link>
        </div>

        <ContextSection item={item} />
        <WorkItemActions item={item} />
        <ActivityLog itemId={item.id} />
      </div>
    </ScrollArea>
  );
}
